<template>
    <div>
        <Panel
            id="route_plans_panel"
            :title="$t('Route Plans')"
            :add-button="$store.hasRight('ADD_ROUTE_PLANS') ? $t('Add') : ''"
            :delete-button="selected.length > 0"
            :help-text="$t('DESC_ROUTE_PLANS')"
            @delete="handleDelete"
            @input="openForm(-1)"
        >
            <ElTabs v-model="activeTab">
                <ElTabPane v-for="type in tabTypes" :name="type.id" :label="$t(type.name)">
                    <table class="cic-table">
                        <thead>
                            <tr>
                                <th>{{ $t('Route Plan Name') }}</th>
                                <th style="width: 70px"></th>
                                <th v-if="$store.hasRight('DELETE_ROUTE_PLANS')" class="td-checkbox"></th>
                            </tr>
                        </thead>
                        <template v-for="category in ['Reusable', 'Non-Reusable']">
                            <thead>
                                <tr>
                                    <th colspan="3">{{ $t(category) }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="plan in items.filter(
                                        (x) =>
                                            x.revisions[0].type === type.id &&
                                            ((category === 'Reusable' && x.reusable) ||
                                                (category !== 'Reusable' && !x.reusable)),
                                    )"
                                    @click="(evt) => handleOpen(plan.planId, evt)"
                                >
                                    <td class="primary">
                                        {{ plan.name }}
                                    </td>
                                    <td>
                                        <b v-if="!plan.inUse">{{ $t('Not In Use') }}</b>
                                    </td>
                                    <td v-if="$store.hasRight('DELETE_ROUTE_PLANS')">
                                        <CicCheckbox
                                            v-if="!plan.inUse"
                                            :value="selected.indexOf(plan.planId) !== -1"
                                            @click="handleSelected(plan.planId)"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </table>
                </ElTabPane>
            </ElTabs>
        </Panel>

        <EditRoutePlan
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            @blur="closePopup"
            @input="saveItem"
            @change="updateFieldValue"
            @changeList="loadPrompts"
            @openRevisions="openRevisions"
        />

        <EditRevision
            v-if="showRevisions"
            :item="item"
            @blur="showRevisions = false"
            @change="(val) => openForm(item.planId, val)"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import { ElTabs, ElTabPane } from 'element-plus';
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import TableUtils from '@/mixins/TableUtils';
    import { deleteItems, load, post, save } from '@/utils/Http';
    import { showValidationMessage } from '@/utils/Notification';
    import {
        getAccessPointWarning,
        getRouteTypes,
        loadInitialPrompts,
        loadPrompts,
        validateRoute,
    } from '@/utils/RouteCollectionUtils';
    import EditRoutePlan from './EditRoutePlan';
    import EditRevision from './EditRevision';
    import './styles/routePlans.scss';

    export default {
        name: 'RoutePlans',
        components: {
            CicCheckbox,
            EditRevision,
            EditRoutePlan,
            ElTabs,
            ElTabPane,
            Panel,
        },
        mixins: [TableUtils],
        data() {
            return {
                activeTab: 1,
                item: null,
                items: [],
                selected: [],
                showRevisions: false,
                types: [],
            };
        },
        computed: {
            tabTypes() {
                return this.types.filter((t) => this.items.some((plan) => plan.revisions[0].type === t.id));
            },
        },
        created() {
            this.$store.clearRouteData();
            this.types = getRouteTypes(0, 0, true);
            this.loadTable();
        },
        methods: {
            handleDelete() {
                deleteItems('routeplans/delete', { planIds: this.selected }, this.loadTable);
            },

            loadPrompts(mediaListId) {
                loadPrompts(mediaListId, post, this.$forceUpdate);
            },

            loadTable() {
                this.selected = [];
                if (this.item) {
                    this.activeTab = this.item.route.type;
                }

                this.closePopup();
                load('routeplans/all', {}, (response) => {
                    this.items = response.plans;
                    if (!this.tabTypes.some((type) => type.id === this.activeTab) && this.tabTypes.length > 0) {
                        this.activeTab = this.tabTypes[0].id;
                    }
                });
            },

            openForm(planId, revisionId = null) {
                let plan = {
                    planId: null,
                    name: '',
                    revision: 0,
                    reusable: true,
                    revisions: [{ rejectCLIR: false, withholdCLI: false }],
                };
                if (planId > 0) {
                    plan = this.items.find((plan) => plan.planId === planId);
                }

                this.showRevisions = false;
                const params = { planId, revisionId, services: this.$store.routeData.mediaLists ? '0' : '1' };
                load('routeplan/revision', params, (response) => {
                    this.item = {
                        planId: plan.planId,
                        name: plan.name,
                        revision: revisionId ? revisionId : plan.revision,
                        reusable: plan.reusable,
                        rejectCLIR: plan.revisions[0].rejectCLIR,
                        withholdCLI: plan.revisions[0].withholdCLI,
                        route: response.revision,
                        revisions: this.item && this.item.planId === planId ? this.item.revisions : null,
                        voiceOnly: response.voiceOnly,
                    };

                    if (response.mediaLists) {
                        this.$store.buildRouteStore(response);
                    }

                    loadInitialPrompts(this.item.route, load, this.$forceUpdate);
                });
            },

            openRevisions() {
                load('routeplan/revisions', { planId: this.item.planId }, (response) => {
                    this.item.revisions = response.revisions;
                    this.showRevisions = true;
                });
            },

            saveItem() {
                if (this.item.name.trim() === '') {
                    showValidationMessage('Route Plan Name is a required field');
                    return;
                }

                if (!validateRoute(this.item.route)) {
                    return;
                }

                const params = {
                    planId: this.item.planId,
                    name: this.item.name,
                    reusable: this.item.reusable ? 1 : 0,
                    rejectCLIR: this.item.rejectCLIR ? 1 : 0,
                    withholdCLI: this.item.withholdCLI ? 1 : 0,
                    ...this.item.route,
                    numbers: JSON.stringify(this.item.route.numbers ?? []),
                    ringingTimeouts: JSON.stringify(this.item.route.ringingTimeouts ?? []),
                    record: this.item.route.record ? 1 : 0,
                    queue: this.item.route.queue ? 1 : 0,
                };

                if (this.item.planId > 0) {
                    let warning = this.$t('ROUTE_WARN_NO_POINTS');
                    load('routeplan/accesspoints', { planIds: [this.item.planId] }, (response) => {
                        if (response.messages[0].total > 0) {
                            warning = getAccessPointWarning(this.$t('ROUTE_WARN_POINTS'), response.messages);
                        }

                        if (confirm(warning)) {
                            save('routeplan/update', params, this.loadTable);
                        }
                    });
                } else {
                    save('routeplan/update', params, this.loadTable);
                }
            },
        },
    };
</script>
